/**
 * Shared vocabulary and pure helpers for composition relationships:
 * aggregation / breakdown as whole → part (parent → child).
 */

import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';

export interface NormalizedCompositionPair {
  wholeItemId: string;
  partItemId: string;
  edgeId: string;
  edgeType: 'aggregation' | 'breakdown';
}

export interface CompositionRoleItemIds {
  /** Items that are wholes with one or more parts. */
  hasParts: Set<string>;
  /** Items that are parts of one or more wholes. */
  isPartOf: Set<string>;
}

function isActiveCompositionEdge(edge: ItemRelationshipEdge): boolean {
  return (
    (edge.type === 'aggregation' || edge.type === 'breakdown') &&
    edge.status !== 'dissolved'
  );
}

/**
 * Normalize aggregation or breakdown to whole → part.
 * Both stored types use source = whole (parent), target = part (child).
 */
export function normalizeCompositionPair(
  edge: ItemRelationshipEdge,
): NormalizedCompositionPair | null {
  if (!isActiveCompositionEdge(edge)) return null;
  return {
    wholeItemId: edge.sourceItemId,
    partItemId: edge.targetItemId,
    edgeId: edge.id,
    edgeType: edge.type as 'aggregation' | 'breakdown',
  };
}

/**
 * Build item-relative presence sets without collapsing whole vs part.
 */
export function getCompositionRoleItemIds(
  edges: ItemRelationshipEdge[],
): CompositionRoleItemIds {
  const hasParts = new Set<string>();
  const isPartOf = new Set<string>();

  for (const edge of edges) {
    const pair = normalizeCompositionPair(edge);
    if (!pair) continue;
    hasParts.add(pair.wholeItemId);
    isPartOf.add(pair.partItemId);
  }

  return { hasParts, isPartOf };
}
